import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

/**
 * Worker Status — operator-facing view of the persistent runtime state.
 *
 * Polls /api/bot/heartbeat every 3 s. The endpoint reads the heartbeat
 * document the bot loop upserts to MongoDB every 5 s, so the dashboard
 * surfaces a true server-side liveness signal (NOT just the WS pulse).
 *
 * Status legend (computed server-side from the configured thresholds):
 *   ALIVE  — last heartbeat younger than ``heartbeat_stale_after_s``
 *   STALE  — between stale and dead thresholds
 *   DEAD   — older than ``heartbeat_dead_after_s`` → BOT OFFLINE.
 */
function fmtAge(s) {
  if (s == null || !isFinite(s)) return "—";
  if (s < 60) return `${s.toFixed(1)}s`;
  if (s < 3600) return `${(s / 60).toFixed(1)}m`;
  if (s < 86400) return `${(s / 3600).toFixed(1)}h`;
  return `${(s / 86400).toFixed(1)}d`;
}
function fmtTs(ts) {
  if (!ts || ts <= 0) return "—";
  return new Date(ts * 1000).toLocaleString();
}

const STATUS_COLORS = {
  alive: "var(--mq-green)",
  stale: "var(--mq-yellow)",
  dead: "var(--mq-red)",
};

export default function WorkerStatusPanel() {
  const { t } = useI18n();
  const [hb, setHb] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);  // initial-fetch flag

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const r = await api.get("/bot/heartbeat");
        if (alive) { setHb(r.data); setErr(""); setLoading(false); }
      } catch (e) {
        if (alive) {
          setLoading(false);
          if (e?.response?.status !== 401) {
            setErr(e?.message || "fetch failed");
          }
        }
      }
    };
    tick();
    const id = setInterval(tick, 3000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // ``loading`` short-circuits the badge so we never flash a red "DEAD"
  // while the first HTTP round-trip is in flight.
  const status = loading ? "loading" : (hb?.heartbeat_status || "dead").toLowerCase();
  const statusColor = loading ? "var(--mq-text-3)" : (STATUS_COLORS[status] || "var(--mq-red)");
  const offlineBanner = !loading && status === "dead" && hb?.bot_running;

  return (
    <div
      data-testid="worker-status-panel"
      className="mq-panel"
      style={{ background: "var(--mq-surface)", border: "1px solid var(--mq-border)" }}
    >
      <div className="px-4 py-2 border-b flex items-center justify-between" style={{ borderColor: "var(--mq-border)" }}>
        <div className="flex items-center gap-3">
          <span className="mq-label" style={{ color: "var(--mq-cyan)" }}>
            {t("workerStatusTitle")}
          </span>
          <span
            data-testid="worker-status-badge"
            className="mq-label px-2 py-0.5 border"
            style={{
              borderColor: statusColor, color: statusColor,
              boxShadow: status === "alive" ? `0 0 6px ${statusColor}` : "none",
            }}
          >
            {status.toUpperCase()}
          </span>
          {offlineBanner && (
            <span
              data-testid="bot-offline-banner"
              className="mq-label px-2 py-0.5"
              style={{ background: "var(--mq-red)", color: "white" }}
            >
              {t("workerBotOffline")}
            </span>
          )}
        </div>
        <span className="mq-label text-[var(--mq-text-3)]">
          v{hb?.backend_version || "?"} · {hb?.supervisor_status || "?"}
        </span>
      </div>

      {err && (
        <div className="px-4 py-1 text-[11px]"
             style={{ background: "rgba(255,64,64,0.07)", color: "var(--mq-red)" }}
             data-testid="worker-status-error">
          {err}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px" style={{ background: "var(--mq-border)" }}>
        <Cell label={t("workerUptime")}        value={fmtAge(hb?.uptime_s)}                testid="worker-uptime" />
        <Cell label={t("workerLastHeartbeat")} value={fmtAge(hb?.heartbeat_age_s)}         testid="worker-last-heartbeat" color={statusColor} />
        <Cell label={t("workerCyclesTotal")}   value={hb?.cycles_total ?? 0}                testid="worker-cycles-total" />
        <Cell label={t("workerErrorsTotal")}   value={hb?.errors_total ?? 0}                testid="worker-errors-total"
              color={(hb?.errors_total || 0) > 0 ? "var(--mq-red)" : undefined} />

        <Cell label={t("workerInstanceId")}    value={hb?.instance_id || "—"}              testid="worker-instance-id" />
        <Cell label={t("workerPid")}           value={hb?.pid ?? "—"}                       testid="worker-pid" />
        <Cell label={t("workerRestartCount")}  value={hb?.restart_count ?? 0}               testid="worker-restart-count"
              color={(hb?.restart_count || 0) > 0 ? "var(--mq-yellow)" : undefined} />
        <Cell label={t("workerAutoRestart")}
              value={hb?.auto_restart_enabled ? "ON" : "OFF"}
              testid="worker-auto-restart"
              color={hb?.auto_restart_enabled ? "var(--mq-green)" : "var(--mq-text-3)"} />

        <Cell label={t("workerDesiredState")}
              value={(hb?.desired_state || "—").toUpperCase()}
              testid="worker-desired-state"
              color={hb?.desired_state === "running" ? "var(--mq-green)" :
                     hb?.desired_state === "emergency_stop" ? "var(--mq-red)" :
                     "var(--mq-text-2)"} />
        <Cell label={t("workerLastRestart")}   value={fmtTs(hb?.last_restart_at)}          testid="worker-last-restart" />
        <Cell label={t("workerLastRestartReason")} value={hb?.last_restart_reason || "—"}  testid="worker-last-restart-reason" />
        <Cell label={t("workerLastChangedBy")} value={hb?.last_changed_by || "—"}          testid="worker-last-changed-by" />
      </div>
    </div>
  );
}

function Cell({ label, value, testid, color }) {
  return (
    <div className="px-4 py-2" style={{ background: "var(--mq-surface)" }} data-testid={testid}>
      <div className="mq-label">{label}</div>
      <div className="mq-metric text-sm" style={{ color: color || "var(--mq-text)" }}>
        {value}
      </div>
    </div>
  );
}
